/**
 * Hitungan layar untuk rock typing — murni, tanpa React.
 *
 * Yang ada di sini adalah yang harus sama di dua tempat sekaligus: warna
 * sebuah kelas pada awan titik dan pada tabelnya di sebelahnya, persamaan yang
 * dicetak pada baris tabel dan garis yang digambar di atas awannya, dan angka
 * yang dituliskan pada legenda. Sebuah kelas yang ungu di tabel dan hijau di
 * plot adalah dua gambar yang tidak berbicara tentang hal yang sama, dan itu
 * satu-satunya alasan hitungan ini tidak tinggal di dalam komponennya.
 *
 * Ruangnya ruang nilai, bukan piksel: pemanggilnya yang tahu seberapa besar
 * kotaknya.
 */

import type { PoroPermFit, RockTypeClass } from "../types";

/**
 * Warna kelas, dari batuan terbaik ke terburuk.
 *
 * Berurut dan bukan sekadar berbeda-beda. Rock type punya arah — RT 1 lebih
 * baik daripada RT 5, selalu — dan sederet warna yang tidak berurut memaksa
 * pembacanya kembali ke legenda pada tiap titik yang dilihatnya. Yang dipakai
 * satu tanjakan dari biru tua (rapat, buruk) ke kuning (lega, baik), arah yang
 * sama dengan yang dipakai peta kerapatan di layar crossplot.
 *
 * Sepuluh warna, sebab dua belas kelas adalah batas server dan dua di
 * ujungnya lalu berulang — dan dua belas jenis batuan pada satu lapangan
 * adalah tanda bahwa yang dipisahkan bukan lagi batuan.
 */
export const CLASS_COLORS = [
  "#f2c14e", "#e8a33d", "#d97e3f", "#c85f4e", "#a8496b",
  "#7f4a86", "#5a4b90", "#3f5591", "#2f6186", "#2b6b6f",
];

/** Warna kelas ke-n. Yang tidak terkelaskan selalu kelabu, dan sengaja pudar. */
export function classColor(index: number): string {
  if (index < 0) return "var(--ink-6)";
  return CLASS_COLORS[index % CLASS_COLORS.length];
}

/**
 * Persamaan φ–k sebuah kelas, sebagaimana orang menuliskannya.
 *
 * Ditulis sebagai k = 10^(a·φ + b) dan bukan sebagai "slope/intercept": yang
 * dipasang adalah regresi pada **log k**, dan sebuah tabel yang mencetak
 * kemiringan tanpa mengatakan kemiringan terhadap apa akan dibaca sebagai
 * mD per unit porositas — yaitu angka yang meleset beberapa dekade.
 */
export function equationOf(fit: PoroPermFit | null): string {
  if (!fit) return "—";
  const sign = fit.intercept < 0 ? "−" : "+";
  return `k = 10^(${fit.slope.toFixed(2)}·φ ${sign} ${Math.abs(fit.intercept).toFixed(2)})`;
}

/** Sebuah angka yang mungkin tidak ada, dengan tempat desimal yang diminta. */
export function num(value: number | null | undefined, digits: number): string {
  return value === null || value === undefined || !Number.isFinite(value)
    ? "—"
    : value.toFixed(digits);
}

/**
 * Permeabilitas sebagaimana ia dibaca orang: dekade, bukan tempat desimal.
 *
 * 0,004 mD dan 2.300 mD muncul pada tabel yang sama, dan aturan desimal yang
 * satu untuk keduanya mencetak salah satunya sebagai "0,00" atau yang lain
 * sebagai "2300,0000".
 */
export function permText(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  if (value >= 1000) return value.toFixed(0);
  if (value >= 10) return value.toFixed(1);
  if (value >= 1) return value.toFixed(2);
  return value.toPrecision(2);
}

/**
 * Titik pada sebuah sumbu logaritmik, 0..1 dari ujung terendah.
 *
 * Yang tidak positif memulangkan NaN dan bukan nol: sebuah permeabilitas nol
 * yang dijepit ke tepi kiri akan berdiri di sana sebagai rombongan yang
 * terbaca sebagai batuan terburuk, padahal yang terjadi adalah ia tidak bisa
 * digambar pada sumbu ini sama sekali.
 */
export function logPlace(value: number, lo: number, hi: number): number {
  if (!(value > 0) || !(lo > 0) || !(hi > lo)) return NaN;
  return (Math.log10(value) - Math.log10(lo)) / (Math.log10(hi) - Math.log10(lo));
}

/** Titik pada sumbu linier, 0..1. */
export function linearPlace(value: number, lo: number, hi: number): number {
  if (!Number.isFinite(value) || !(hi > lo)) return NaN;
  return (value - lo) / (hi - lo);
}

/**
 * Ujung sumbu permeabilitas: dekade penuh yang memuat seluruh datanya.
 *
 * Dibulatkan ke dekade supaya tanda sumbunya jatuh pada 0,1 · 1 · 10 · 100 —
 * angka yang langsung terbaca — dan bukan pada 0,37 dan 41,9. Awan yang
 * seluruhnya di dalam satu dekade tetap mendapat satu dekade penuh: sumbu
 * setinggi 0,3 dekade meregangkan hamburan menjadi tren.
 */
export function decadeBounds(values: number[]): [number, number] | null {
  const positive = values.filter((v) => Number.isFinite(v) && v > 0);
  if (!positive.length) return null;
  const lo = Math.floor(Math.log10(Math.min(...positive)));
  const hi = Math.ceil(Math.log10(Math.max(...positive)));
  return [10 ** lo, 10 ** (hi > lo ? hi : lo + 1)];
}

/** Tanda sumbu logaritmik: satu per dekade, ujungnya ikut. */
export function decadeTicks(lo: number, hi: number): number[] {
  if (!(lo > 0) || !(hi > lo)) return [];
  const ticks: number[] = [];
  for (let e = Math.ceil(Math.log10(lo)); e <= Math.floor(Math.log10(hi) + 1e-9); e++) {
    ticks.push(10 ** e);
  }
  return ticks;
}

/**
 * Garis regresi sebuah kelas, dipotong ke porositas yang benar-benar ada.
 *
 * Dipotong dan tidak diperpanjang, dengan alasan yang sama seperti pada
 * crossplot: sebuah garis yang membentang sampai tepi kotak menyatakan
 * hubungan pada porositas yang tidak punya satu pun sampel di dalam kelas ini,
 * dan pada plot poroperm itulah bagian yang paling mudah dipercaya orang —
 * porositas rendah, tempat permeabilitas diekstrapolasi turun beberapa dekade.
 */
export function fitLine(fit: PoroPermFit | null): { phi: number[]; perm: number[] } | null {
  if (!fit || !(fit.phi_max > fit.phi_min)) return null;
  const steps = 24;
  const phi: number[] = [];
  const perm: number[] = [];
  for (let i = 0; i <= steps; i++) {
    const x = fit.phi_min + ((fit.phi_max - fit.phi_min) * i) / steps;
    phi.push(x);
    perm.push(10 ** (fit.slope * x + fit.intercept));
  }
  return { phi, perm };
}

/**
 * Kelas yang benar-benar punya sampel, untuk legenda dan untuk menu.
 *
 * Tabel memajang kelas kosong — ketiadaan sebuah kelas port adalah temuannya —
 * tetapi legenda tidak: sebuah legenda yang menyebut lima warna sementara tiga
 * di antaranya tidak pernah tergambar menyuruh pembacanya mencari yang tidak
 * ada.
 */
export function occupied(classes: RockTypeClass[]): RockTypeClass[] {
  return classes.filter((row) => row.index >= 0 && row.n > 0);
}

/**
 * Apakah sapuan siluetnya membantah jumlah kelas yang sedang dipakai.
 *
 * Dipulangkan sebagai kalimat dan bukan sebagai boolean, sebab yang berguna
 * bagi pembacanya adalah angka pembandingnya. Ia saran dan bukan larangan:
 * berapa jenis batuan yang ada adalah pertanyaan geologi, dan siluet hanya
 * tahu seberapa rapi datanya terbelah.
 */
export function sweepVerdict(
  suggested: number | null,
  asked: number,
): string | null {
  if (suggested === null || suggested === asked) return null;
  return `The silhouette separates most cleanly at ${suggested} classes, not ${asked}. ` +
    `That is a remark about how the data splits, not about how many rock types the reservoir has.`;
}
